import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const milestones = [
  { year: '2018', title: '品牌创立', description: '星空影院正式成立，以"让每一个人都能轻松看好电影"为使命，开启在线观影新时代。' },
  { year: '2019', title: '资源整合', description: '与多家国内外知名影视内容提供商建立合作关系，电影资源库突破10000部。' },
  { year: '2020', title: '技术升级', description: '引入CDN加速技术，实现全站视频播放不缓冲，用户观影体验大幅提升。' },
  { year: '2021', title: '院线同步', description: '开创院线同步更新模式，让线上用户也能第一时间观看最新上映的热门电影。' },
  { year: '2022', title: '用户破千万', description: '平台注册用户突破1000万，月活跃用户超5000万，成为国内领先的在线电影平台。' },
  { year: '2023', title: 'AI推荐系统', description: '上线智能推荐算法，根据用户观影偏好精准推荐电影，用户满意度达98%。' },
  { year: '2024', title: '全面焕新', description: '品牌全面升级，推出全新界面设计和交互体验，电影排行榜实时更新覆盖更广。' },
  { year: '2025', title: '全球化布局', description: '业务拓展至东南亚、北美等海外市场，服务全球华人影迷，迈向国际化发展新阶段。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '影评人',
    content: '星空影院的电影更新速度令人惊叹，院线刚上映的片子很快就能在这里找到。画质清晰，播放流畅，真的是我每天必刷的电影网站。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '影视爱好者',
    content: '作为一个影迷，星空影院完全满足了我的观影需求。分类齐全、排行榜更新及时，而且完全免费！推荐给所有爱看电影的朋友。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '大学生',
    content: '在学校宿舍用星空影院看电影，从来没卡过。电影资源特别丰富，从经典老片到最新上映都有，课余时间的好伙伴。',
    rating: 5,
  },
  {
    name: '赵女士',
    role: '白领',
    content: '下班后的放松方式就是打开星空影院看一部好电影。排行榜功能特别好用，能快速找到大家都在看的热门电影，不踩雷。',
    rating: 4,
  },
  {
    name: '陈先生',
    role: 'IT工程师',
    content: '从技术角度来说，星空影院的视频加载速度真的很厉害，不缓冲这个卖点名不虚传。界面设计也很简洁好用，用户体验一流。',
    rating: 5,
  },
  {
    name: '刘女士',
    role: '全职妈妈',
    content: '在家带孩子间隙看看电影放松一下，星空影院分类清晰，能找到适合孩子看的动画片，也能找到自己喜欢看的剧情片，全家都喜欢！',
    rating: 5,
  },
];

const faqData = [
  {
    question: '星空影院是一个怎样的平台？',
    answer: '星空影院是国内领先的免费在线电影观看平台。我们提供热播电影、热门电影、最新上映电影的高清在线观看服务，支持多端观看，院线电影同步更新，电影排行榜实时推荐，让您随时随地畅享优质观影体验。',
  },
  {
    question: '星空影院的电影资源是否正版？',
    answer: '星空影院严格遵守版权法规，所有提供的电影资源均来自合法渠道。我们与多家影视内容提供商保持长期合作关系，确保平台上的每一部电影都有合法的授权来源。',
  },
  {
    question: '如何联系星空影院的客服团队？',
    answer: '您可以通过以下方式联系我们的客服团队：发送邮件至 contact@xingkongcinema.com，或拨打客服热线 400-888-6666。我们的客服工作时间为周一至周日 9:00-22:00，节假日照常服务。',
  },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 星空影院 | 品牌介绍与联系方式';
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 cursor-pointer whitespace-nowrap transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-300">关于我们</span>
            </nav>

            <section className="mb-14">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  关于星空影院
                </h1>
                <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
                  星空影院成立于2018年，致力于为广大影迷提供最新、最全、最优质的免费在线观影体验。我们坚持"高清不缓冲、院线同步看"的服务理念，已累计服务超过五千万用户。
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">8年+</div>
                  <p className="text-xs text-foreground-500">行业深耕</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">5000万+</div>
                  <p className="text-xs text-foreground-500">服务用户</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">30000+</div>
                  <p className="text-xs text-foreground-500">电影资源</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">98%</div>
                  <p className="text-xs text-foreground-500">用户好评</p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6 text-center">
                品牌历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/60 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((item, index) => (
                    <div key={index} className={`flex flex-col md:flex-row items-start gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 hidden md:block"></div>
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold z-10">
                        {item.year.slice(2)}
                      </div>
                      <div className="flex-1 p-4 rounded-lg bg-background-100/80 border border-background-200/60 w-full">
                        <div className="text-xs text-primary-500 font-semibold mb-1">{item.year}</div>
                        <h3 className="text-sm font-semibold text-foreground-100 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((t, index) => (
                  <div key={index} className="p-5 rounded-lg bg-background-100/80 border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className={`text-xs ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-600'}`}></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed mb-4">{t.content}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-500 text-xs font-bold">
                        {t.name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-100">{t.name}</p>
                        <p className="text-xs text-foreground-600">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@xingkongcinema.com</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-500">400-888-6666</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100/80 border border-background-200/60 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">北京市朝阳区星光大道88号</p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6 text-center">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto grid gap-3">
                {faqData.map((faq, index) => (
                  <details key={index} className="group bg-background-100/80 border border-background-200/60 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-200/30 transition-colors">
                      <span className="text-sm font-medium text-foreground-100 pr-4">{faq.question}</span>
                      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                        <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                        <i className="ri-subtract-line text-primary-500 hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-foreground-400 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '星空影院',
            url: 'https://xingkongcinema.com/',
            description: '星空影院提供热播电影、热门电影在线观看，院线电影同步更新，最新上映影片一网打尽，电影排行榜实时推荐，高清视频不缓冲免费看。',
            foundingDate: '2018',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+86-400-888-6666',
              email: 'contact@xingkongcinema.com',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '星光大道88号',
              addressLocality: '北京',
              addressRegion: '朝阳区',
              addressCountry: 'CN',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqData.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://xingkongcinema.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}